import { Helmet } from 'react-helmet-async'
import { siteConfig } from '@/config/site'

interface SeoProps {
  title?: string
  description?: string
  path?: string
  image?: string
  noIndex?: boolean
  /** Extra JSON-LD schema(s) to merge alongside the site-wide WebSite schema. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

const defaultJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteConfig.name,
  url: siteConfig.url,
  description: siteConfig.description,
}

export function Seo({
  title,
  description = siteConfig.description,
  path = '/',
  image = siteConfig.ogImage,
  noIndex = false,
  jsonLd,
}: SeoProps) {
  const pageTitle = title ? `${title} | ${siteConfig.name}` : siteConfig.title
  const url = new URL(path, siteConfig.url).toString()
  const imageUrl = new URL(image, siteConfig.url).toString()
  const schemas = [defaultJsonLd, ...(jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [])]

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteConfig.name} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content={siteConfig.locale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={siteConfig.twitterHandle} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  )
}
